import React, { useEffect, useState } from 'react';
import { Modal, Button, Table, Header, Pagination, Message, Input, Toast, Icon } from 'react-frontier';
import { PromoterContact, SetLoading } from '../SiteClasses';
import API from '../API';
import Validator from '../Validator';
import { bindFormChange, bindClick } from '../SiteUtil';

interface SearchPromoterContactModalProps{
	open: boolean;
	onClose: () => void;
	onSelected: (contact: PromoterContact) => void;
	promoter_id: number;
	title?: string;
}

interface ContactForm{
	contact_name: string;
	email: string;
	phone: string;
	department: string;
}

const PAGE_SIZE = 30;

var SearchPromoterContactModal = (props: SearchPromoterContactModalProps)=>{
	var [contacts, setContacts] = useState<PromoterContact[]>([]);
	var [loading, setLoading] = useState<boolean>(false);
	var [page, setPage] = useState<number>(0);
	var [modalPrompts, setModalPrompts] = useState<string[]>(null);
	var [showCreateModal, setShowCreateModal] = useState<boolean>(false);
	var [form, setForm] = useState<ContactForm>({ contact_name: '', email: '', phone: '', department: '' });
	var [createModalPrompts, setCreateModalPrompts] = useState<string[]>(null);

	useEffect(()=>{
		if(props.open && props.promoter_id){
			get();
		}
	}, [props.open, props.promoter_id, page]);

	var get = ()=>{
		setLoading(true);
		setModalPrompts(null);
		API.getPromoterContacts(props.promoter_id, page * PAGE_SIZE, PAGE_SIZE).then(res=>{
			if(res.error) return setModalPrompts([res.message]);
			setContacts(res.data);
		}).catch(err=>{
			setModalPrompts(['Hubo un error cargando los contactos (LCL-1)']);
		}).finally(()=>{
			setLoading(false);
		});
	};

	var onContactSelected = (contact: PromoterContact)=>{
		props.onSelected(contact);
	};

	var onPageChange = (newPage: number)=>{
		setPage(newPage);
	};

	var onOpenCreateModal = ()=>{
		setForm({ contact_name: '', email: '', phone: '', department: '' });
		setCreateModalPrompts(null);
		setShowCreateModal(true);
	};

	var onSubmitCreate = (setLoading: SetLoading)=>{
		var { valid, prompts } = Validator(form, {
			contact_name: [{
				rule: 'minLength', params: [3], label: 'Nombre de contacto'
			}],
			phone: [{
				rule: 'phone', label: 'Teléfono'
			}],
			department: [{
				rule: 'minLength', params: [3], label: 'Departamento'
			}],
		});
		setCreateModalPrompts(prompts);
		if(!valid) return;
		setLoading(true);

		API.createPromoterContact(props.promoter_id, form.contact_name, form.email, form.phone, form.department).then(res=>{
			if(res.error) return setCreateModalPrompts([res.message]);
			Toast.success('Contacto agregado!');

			var newContact: PromoterContact = {
				promoter_id: props.promoter_id,
				contact_id: res.data.contact_id,
				contact_name: form.contact_name,
				email: form.email,
				phone: form.phone,
				department: form.department,
				deleted: false,
			};

			setContacts([newContact, ...contacts]);
			setShowCreateModal(false);
		}).catch(err=>{
			setCreateModalPrompts(['Hubo un error inesperado creando el contacto (LCL-2)']);
		}).finally(()=>{
			setLoading(false);
		});
	};

	var onFormChange = bindFormChange(form, setForm);

	return <>
		<Modal open={props.open} onClose={props.onClose} size="small">
			<Modal.Header>
				<div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
					{props.title || 'Seleccionar contacto'}
					<Button iconName='plus-circle' text="Agregar" size='small' color='black' onClick={onOpenCreateModal}/>
				</div>
			</Modal.Header>
		<Modal.Content>
			<Table 
				fitted 
				striped
				collapsingIndexes={[1, 3]}
				headers={['Nombre', 'Departamento', 'Email', 'Teléfono']}
				footer={
					contacts.length >= PAGE_SIZE && (
						<Table.Cell colSpan={4} row>
							<div style={{ display: 'flex', justifyContent: 'flex-end', width: '100%' }}>
								<Pagination 
									page={page} 
									onPageChange={onPageChange} 
									hasNext={contacts.length >= PAGE_SIZE} 
								/>
							</div>
						</Table.Cell>
					)
				}
			>
				{loading ? (
					<Table.Row>
						<Table.Cell colSpan={4}>
							<Header loading text="Cargando contactos..." />
						</Table.Cell>
					</Table.Row>
				) : contacts.length > 0 ? (
					contacts.map(contact=>(
						<Table.Row selectable
							collapsingIndexes={[1, 2, 3]}
							key={contact.contact_id}
							onClick={()=>onContactSelected(contact)}
							data={[
								contact.contact_name,
								contact.department || <Icon name='minus'/>,
								contact.email || <Icon name='minus'/>,
								contact.phone || <Icon name='minus'/>,
							]}
						/>
					))
				) : (
					<Table.Row>
						<Table.Cell colSpan={4} className="empty">
							El promotor no tiene contactos creados
						</Table.Cell>
					</Table.Row>
				)}
			</Table>
			<Message style={{ marginTop: 15 }} type='error' list={modalPrompts} />
		</Modal.Content>
		<Modal.Actions>
			<Button basic text="Cerrar" onClick={props.onClose} />
		</Modal.Actions>
	</Modal>
	<Modal open={showCreateModal} size="tiny" onClose={bindClick(setShowCreateModal, false)}>
		<Modal.Header>Crear contacto</Modal.Header>
		<Modal.Content>
			<Input label='Nombre' value={form.contact_name} onChange={onFormChange('contact_name')} />
			<Input label='Departamento' value={form.department} onChange={onFormChange('department')} />
			<Input label='Correo electrónico' value={form.email} onChange={onFormChange('email')} />
			<Input label='Teléfono' value={form.phone} onChange={onFormChange('phone')} />
			<Message style={{ marginTop: 15 }} type='error' list={createModalPrompts} />
		</Modal.Content>
		<Modal.Actions>
			<Button onClick={bindClick(setShowCreateModal, false)} text='Cerrar' basic/>
			<Button color="black" onClick={onSubmitCreate} text='Guardar'/> 
		</Modal.Actions>
	</Modal>
</>
}

export default SearchPromoterContactModal;
